import React, { useState, useRef, useEffect } from 'react';

type Language = 'en' | 'es' | 'fr';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const greetings: Record<Language, string> = {
  en: "How can I help?",
  es: "¿Cómo puedo ayudarte?",
  fr: "Comment puis-je vous aider?",
};

const placeholders: Record<Language, string> = {
  en: 'Ask anything...',
  es: 'Pregunta lo que quieras...',
  fr: 'Posez votre question...',
};

const fallbacks: Record<Language, string> = {
  en: "I'm having a bit of trouble connecting right now. Please try the FAQ section above, or email us at support@aidfone.com — I'll make sure someone gets back to you!",
  es: "Tengo problemas para conectarme en este momento. Consulta la sección de preguntas frecuentes arriba, o escríbenos a support@aidfone.com — ¡nos aseguraremos de responderte!",
  fr: "J'ai de la difficulté à me connecter. Consultez la FAQ ci-dessus ou écrivez à support@aidfone.com — je m'assure qu'on vous réponde!",
};

const disclaimers: Record<Language, string> = {
  en: 'AidFone Assistant — For product and account questions only. Not a medical tool.',
  es: 'Asistente AidFone — Solo para preguntas sobre el producto y la cuenta. No es una herramienta médica.',
  fr: 'Assistant AidFone — Pour les questions sur le produit et le compte seulement. Ce n\'est pas un outil médical.',
};

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="w-5 h-5 text-white">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

interface AidFoneAssistantProps {
  visitorType?: 'prospect' | 'customer';
}

export const AidFoneAssistant: React.FC<AidFoneAssistantProps> = ({ visitorType = 'prospect' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState<Language>('en');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const languageRef = useRef(language);
  languageRef.current = language;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Ref for auto-send from chip clicks (set after sendMessage is defined below)
  const sendMessageRef = useRef<(text?: string) => void>(undefined);

  useEffect(() => {
    const handlePrefill = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      if (!detail || typeof detail.text !== 'string') return;
      const text = detail.text.trim();
      setIsOpen(true);
      setMessages((prev) =>
        prev.length === 0
          ? [{ role: 'assistant', content: greetings[languageRef.current] }]
          : prev
      );
      if (text && detail.autoSend) {
        // Auto-send: small delay so greeting renders first
        setTimeout(() => sendMessageRef.current?.(text), 300);
      } else if (text) {
        setInputValue(text);
      }
    };

    window.addEventListener('aidfone-chat-prefill', handlePrefill);
    return () => window.removeEventListener('aidfone-chat-prefill', handlePrefill);
  }, []);

  const toggleChat = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: greetings[language] }]);
    }
  };

  const sendMessage = async (overrideText?: string) => {
    const text = overrideText || inputValue.trim();
    if (!text || isTyping) return;

    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const userMessage: ChatMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsTyping(true);

    try {
      // Get the API URL from environment or use relative path
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/chat/sophie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
          language,
          visitorType,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        setMessages([...newMessages, { role: 'assistant', content: fallbacks[language] }]);
        setIsTyping(false);
        return;
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        setMessages([...newMessages, { role: 'assistant', content: fallbacks[language] }]);
        setIsTyping(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let hasStartedStreaming = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.text) {
                fullContent += data.text;

                if (!hasStartedStreaming) {
                  // First chunk: hide typing indicator and add message
                  hasStartedStreaming = true;
                  setIsTyping(false);
                  setMessages([...newMessages, { role: 'assistant', content: fullContent }]);
                } else {
                  // Subsequent chunks: update the last message
                  setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: fullContent };
                    return updated;
                  });
                }
              }

              if (data.error) {
                setMessages([...newMessages, { role: 'assistant', content: fallbacks[language] }]);
                setIsTyping(false);
                break;
              }
            } catch {
              // Skip non-JSON lines
            }
          }
        }
      }

      // If no content was received, show fallback
      if (!fullContent) {
        setMessages([...newMessages, { role: 'assistant', content: fallbacks[language] }]);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setMessages([...newMessages, { role: 'assistant', content: fallbacks[language] }]);
    }

    setIsTyping(false);
  };

  // Keep ref in sync so the event listener can call sendMessage
  sendMessageRef.current = sendMessage;

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="aidfone-assistant-root fixed bottom-6 right-6 z-[9999] font-body">
      {/* Toggle Button */}
      <button
        onClick={toggleChat}
        className={`border-2 cursor-pointer shadow-[0_4px_20px_rgba(0,0,0,0.15)] flex items-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_25px_rgba(0,0,0,0.2)] hover:border-brand-primary ${
          isOpen
            ? 'rounded-full p-3 w-12 h-12 justify-center bg-brand-primary border-brand-primary'
            : 'rounded-full px-1.5 pr-4 py-1.5 bg-white border-brand-border'
        }`}
        aria-label="Chat with AidFone Assistant"
      >
        {isOpen ? (
          <span className="text-white"><CloseIcon /></span>
        ) : (
          <>
            <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 relative">
              <img
                src="/images/landing/aidfone-favicon.png"
                alt="AidFone Assistant"
                className="w-6 h-6 object-contain"
                width={24}
                height={24}
              />
              <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2.5 h-2.5 bg-brand-success border-[1.5px] border-white rounded-full" />
            </div>
            <div className="flex flex-col text-left hidden sm:flex">
              <span className="font-semibold text-sm text-brand-text leading-tight">AidFone Assistant</span>
            </div>
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-20 right-0 w-[380px] max-w-[calc(100vw-48px)] h-[520px] max-h-[calc(100vh-120px)] bg-white rounded-lg shadow-[0_10px_50px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-brand-primary text-white px-5 py-4 flex items-center gap-3">
            <div className="w-[50px] h-[50px] rounded-lg flex items-center justify-center overflow-hidden border-2 border-white/30 bg-white/10">
              <img
                src="/images/landing/aidfone-favicon.png"
                alt=""
                className="w-8 h-8 object-contain"
                width={32}
                height={32}
              />
            </div>
            <div>
              <h4 className="text-base font-semibold mb-0.5 font-body">AidFone Assistant</h4>
            </div>
            <div className="ml-auto flex gap-1">
              {(['en', 'es', 'fr'] as Language[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  className={`px-2 py-1 rounded text-[0.7rem] font-semibold cursor-pointer transition-all border-none ${
                    language === lang
                      ? 'bg-white/35 text-white'
                      : 'bg-white/15 text-white/70 hover:bg-white/25 hover:text-white'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 bg-brand-bg-warm">
            <div className="px-3 py-2 text-center rounded-lg border border-brand-border bg-brand-bg" style={{ fontSize: '12px' }}>
              <span className="text-brand-text-muted">{disclaimers[language]}</span>
            </div>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`max-w-[85%] px-4 py-3 rounded-2xl text-[0.9rem] leading-relaxed ${
                  msg.role === 'assistant'
                    ? 'bg-white text-brand-text border border-brand-border self-start rounded-bl'
                    : 'bg-brand-primary text-white self-end rounded-br'
                }`}
              >
                {msg.content}
              </div>
            ))}
            {isTyping && (
              <div className="bg-white border border-brand-border self-start rounded-2xl rounded-bl px-4 py-3">
                <div className="flex gap-1 py-1">
                  <span className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce" />
                  <span className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce [animation-delay:0.2s]" />
                  <span className="w-2 h-2 bg-brand-text-muted rounded-full animate-bounce [animation-delay:0.4s]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-brand-border flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={placeholders[language]}
              className="flex-1 border border-brand-border rounded-full px-4 py-3 text-[0.9rem] font-body outline-none transition-colors focus:border-brand-primary placeholder:text-brand-text-muted"
            />
            <button
              onClick={() => sendMessage()}
              disabled={isTyping || !inputValue.trim()}
              className={`w-11 h-11 rounded-full border-none cursor-pointer flex items-center justify-center transition-all ${
                isTyping || !inputValue.trim()
                  ? 'bg-brand-border cursor-not-allowed'
                  : 'bg-brand-accent hover:bg-[#c96a3f]'
              }`}
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease;
        }
      `}</style>
    </div>
  );
};
